<script setup lang="ts">
import { computed, ref } from 'vue'
import { mdiContentSave } from '@mdi/js'
import { useUserStore } from '@/modules/user/stores/user.store'
import { useClientStore } from '@/modules/client/stores/client.store'
import DropdownList from '@/shared/components/dropdown/DropdownList.vue'
import BaseModal from '@/shared/components/modal/BaseModal.vue'
import HeaderModal from '@/shared/components/modal/HeaderModal.vue'
import BodyModal from '@/shared/components/modal/BodyModal.vue'
import FooterModal from '@/shared/components/modal/FooterModal.vue'
import PrimaryButton from '@/shared/components/button/PrimaryButton.vue'
import DefaultButton from '@/shared/components/button/DefaultButton.vue'

const open = defineModel<boolean>('open', { required: true })
const clientStore = useClientStore()
const userStore = useUserStore()
const isSaving = ref<boolean>(false)
const viewId = ref<number>()

const isDirty = computed((): boolean => viewId.value !== clientStore.defaultViewId)

const onLoad = () => {
  if (clientStore.defaultViewId) {
    viewId.value = clientStore.defaultViewId
  }
}

const onClose = () => {
  open.value = false
}

const onUpdate = async (): Promise<void> => {
  if (viewId.value) {
    isSaving.value = true
    const isSuccess = await userStore.updateUserSetting({
      languageId: 1,
      defaultViewId: viewId.value,
    })

    if (isSuccess) {
      clientStore.defaultViewId = viewId.value
    }
    isSaving.value = false
  }
}

onLoad()
</script>

<template>
  <BaseModal :open="open" :confirm="isDirty" @confirm="onClose()">
    <HeaderModal :title="$t('glossary.mySetting', 2)" @close="onClose()" />
    <BodyModal>
      <DropdownList
        v-if="clientStore.views"
        v-model="viewId"
        item-key="viewId"
        item-text="viewName"
        :title="$t('glossary.defaultApplicationView')"
        :label="true"
        :items="clientStore.views"
      />
    </BodyModal>
    <FooterModal class="justify-end">
      <DefaultButton
        :text="isDirty ? $t('common.cancel') : $t('common.close')"
        @click="onClose()"
      />
      <PrimaryButton
        :text="$t('common.save')"
        class="ms-2"
        :icon="mdiContentSave"
        :loading="isSaving"
        :disabled="!isDirty"
        @click="onUpdate()"
      />
    </FooterModal>
  </BaseModal>
</template>
